/**
 * useFCM Hook — Foreground Push Notification Handler
 * 
 * Handles:
 * - Listening for foreground FCM messages
 * - Displaying toast notifications when the app is in focus
 * - Refreshing FCM tokens on page visibility change
 * - Handling notification click navigation from the service worker
 * - Deep-linking to correct page based on notification data
 */

import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { onMessage } from 'firebase/messaging';
import toast from 'react-hot-toast';
import { getFirebaseMessaging } from '../config/firebase';
import { registerFcmTokenIfNeeded, refreshFcmTokenIfNeeded } from '../services/fcmService';

/**
 * Resolve a notification data payload to a web route path + state.
 * Notification data fields (from chat server):
 *   - chatId, chatType, senderId, messageId, type (chat messages / file uploads)
 *   - type='group_deleted', groupId, groupName (group deletion)
 */
const resolveNotificationRoute = (data) => {
  if (!data) return { path: '/dashboard', state: null };

  // Chat message or file upload notification
  if (data.chatId) {
    return {
      path: '/communication/chat',
      state: {
        notificationChatId: data.chatId,
        notificationChatType: data.chatType || 'private',
        notificationSenderId: data.senderId || null,
        notificationMessageId: data.messageId || null,
      },
    };
  }

  // Group deletion notification
  if (data.type === 'group_deleted') {
    return {
      path: '/communication/chat',
      state: { notificationGroupDeleted: data.groupId || true },
    };
  }

  // Default — go to dashboard
  return { path: '/dashboard', state: null };
};

/**
 * Custom hook to manage FCM push notifications in the web app.
 * Should be called once in the App component after authentication.
 */
const useFCM = () => {
  const token = useSelector((state) => state.auth?.token);
  const chatToken = useSelector((state) => state.auth?.chatToken);
  const unsubscribeRef = useRef(null);
  const navigate = useNavigate();

  // Register FCM token after login (when both auth token and chat token are available)
  useEffect(() => {
    if (!token || !chatToken) return;

    const initFCM = async () => {
      try {
        await registerFcmTokenIfNeeded();
      } catch (error) {
        console.error('FCM initialization failed:', error);
      }
    };

    initFCM();
  }, [token, chatToken]);

  // Set up foreground message listener
  useEffect(() => {
    if (!token || !chatToken) return;

    let isMounted = true;

    const setupForegroundListener = async () => {
      try {
        const messaging = await getFirebaseMessaging();
        if (!messaging || !isMounted) return;

        // Listen for foreground messages
        unsubscribeRef.current = onMessage(messaging, (payload) => {
          console.log('Foreground FCM message received:', payload);

          const title = payload.notification?.title || payload.data?.title || 'Proton';
          const body = payload.notification?.body || payload.data?.body || 'You have a new notification';
          const data = payload.data || {};

          // Resolve where to navigate on click
          const { path, state } = resolveNotificationRoute(data);

          // Show an in-app toast notification
          toast(
            (t) => (
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate(path, { state });
                }}
              >
                <strong>{title}</strong>
                <div style={{ fontSize: '13px', marginTop: 4, opacity: 0.85 }}>{body}</div>
              </div>
            ),
            {
              duration: 5000,
              position: 'top-right',
              icon: '🔔',
              style: {
                borderRadius: '10px',
                background: '#fff',
                color: '#333',
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                border: '1px solid #e2e8f0',
                padding: '12px 16px',
              },
            }
          );

          // Also try to show a browser notification if page is not focused
          if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
            new Notification(title, {
              body,
              icon: '/logo192.png',
              tag: data.chatId || 'proton-foreground',
            });
          }
        });
      } catch (error) {
        console.error('Error setting up foreground FCM listener:', error);
      }
    };

    setupForegroundListener();

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [token, chatToken, navigate]);

  // Refresh FCM token on page visibility change (mirrors mobile app's AppState listener)
  useEffect(() => {
    if (!token || !chatToken) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Page became visible — refresh token (handles token expiry)
        refreshFcmTokenIfNeeded();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, chatToken]);

  // Handle notification click messages from the service worker
  useEffect(() => {
    if (!navigate) return;

    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        const { data } = event.data;
        const { path, state } = resolveNotificationRoute(data);
        navigate(path, { state });
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [navigate]);
};

export default useFCM;
